/**
 * api/lem.ts
 * ──────────
 * Client for the Location Enhancement Module (LEM) admin endpoints.
 * All endpoints are admin/super_admin gated server-side (RequireAdmin).
 */

import { apiClient } from './client';

export type LemFaultType =
  | 'missing'
  | 'damaged'
  | 'swapped'
  | 'misplaced'
  | 'single_misread'
  | 'shifted_block'
  | 'ok';

export interface LemWorklistRow {
  location_code: string;
  fault_type: LemFaultType;
  swapped_with: string | null;
  confidence: number;
  confidence_label: 'low' | 'medium' | 'high';
  decode_success_rate: number;
  photos_total: number;
  photos_decoded: number;
  photos_correct: number;
  decode_failures: number;
  evidence: string;
  recommended_fix: string;
  comments: string;
  verified: boolean;
  verification_status: string | null;
  verified_by_name: string | null;
  verified_at: string | null;
  verification_notes: string | null;
  /** Populated only on a 'shifted_block' row: the member slot codes it rolls up. */
  member_locations: string[] | null;
}

export interface LemWorklist {
  warehouse_id: string;
  mission_ids: string[];
  photos_analysed: number;
  locations_assessed: number;
  overall_decode_rate: number;
  summary: Record<string, number>;
  rows: LemWorklistRow[];
}

export type LemStatusFilter = 'all' | 'open' | 'verified';

export async function fetchWorklist(
  warehouseId: string,
  status: LemStatusFilter = 'all',
  includeOk = false,
): Promise<LemWorklist> {
  const qs = new URLSearchParams({
    warehouse_id: warehouseId,
    status,
    include_ok: String(includeOk),
  });
  return apiClient.get<LemWorklist>(`/api/lem/worklist?${qs.toString()}`);
}

export interface VerifyResult {
  location_code: string;
  status: string;
  verified_by_name: string | null;
  verified_at: string | null;
  notes: string | null;
}

export async function verifyLocation(body: {
  warehouse_id: string;
  location_code: string;
  status: 'verified' | 'open';
  fault_type?: string | null;
  decode_rate?: number | null;
  notes?: string | null;
}): Promise<VerifyResult> {
  return apiClient.post<VerifyResult>('/api/lem/locations/verify', body);
}

/** Trigger a CSV download of the worklist (auth header injected via getRaw). */
export async function downloadWorklistCsv(
  warehouseId: string,
  status: LemStatusFilter = 'all',
  includeOk = false,
): Promise<void> {
  const qs = new URLSearchParams({
    warehouse_id: warehouseId,
    status,
    include_ok: String(includeOk),
  });
  const res = await apiClient.getRaw(`/api/lem/worklist/export.csv?${qs.toString()}`);
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `qr_worklist_${warehouseId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
